import {
  capitalizeTerm,
  countryDisplayName,
  formatAmount,
  formatAmountWithUnit,
  USD,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { CurrencyIcon } from "@loot-os/ui/components/currency-icon";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { BattleModeBadge } from "../components/battles/BattleModeBadge.js";
import { BattleRoundsStrip, type RoundBox } from "../components/battles/BattleRoundsStrip.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { RedeemGiftCodeForm } from "../components/RedeemGiftCodeForm.js";
import { RequireAuth } from "../components/RequireAuth.js";
import { ShippingAddressDialog } from "../components/ShippingAddressDialog.js";
import { UsernameDialog } from "../components/UsernameDialog.js";
import { track } from "../lib/analytics.js";
import { spendableCents } from "../lib/balance.js";
import { BATTLE_OUTCOME_LABEL, battleOutcome, viewerSeat } from "../lib/battleOutcome.js";
import { useCurrency } from "../lib/currency.js";
import { kycUiState } from "../lib/kyc.js";
import { useProfile } from "../lib/profile.js";
import {
  balancePacksQueryOptions,
  boxesQueryOptions,
  lootMeQueryOptions,
  myBattlesInfiniteQueryOptions,
  opensInfiniteQueryOptions,
  PURCHASE_POLL_LIMIT,
  purchasePollQueryOptions,
  qk,
} from "../lib/queries.js";
import { queryClient } from "../lib/queryClient.js";
import { useUpdateAutoClaim } from "../lib/revealClaim.js";
import {
  addressLines,
  shippingDestinationsOf,
  useShippingAddress,
} from "../lib/shippingAddress.js";
import { useBalance, useShopper, useStartCheckout, useStartKyc } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import { useTerminology } from "../lib/terminology.js";

export function AccountPage() {
  return (
    <RequireAuth>
      <AccountContent />
    </RequireAuth>
  );
}

/** GET /v1/me, /v1/me/balance, /v1/me/opens, /v1/me/battles, /v1/me/shipping-address, PUT /v1/me/settings and POST /v1/me/gift-codes/redeem — the session-token surface. */
function AccountContent() {
  const { user, sessionToken } = useShopper();
  const { showProfile } = useProfile();
  const { config, hasFeature } = useTenant();
  const currency = useCurrency();
  const unit = currency.namePlural;
  const { box, item, battle } = useTerminology();
  const [error, setError] = useState<string | null>(null);

  const balance = useBalance().data ?? null;
  // Stores that require identity verification refetch on every return to the tab: the Veriff
  // flow runs in another tab and its decision arrives through a webhook, so this is how the card
  // moves from "pending" to "verified" without a reload (boxed's shopper has to reload).
  const kycRequired = config.kycRequired;
  const me =
    useQuery({
      ...lootMeQueryOptions(),
      enabled: !!sessionToken,
      refetchOnWindowFocus: kycRequired ? "always" : undefined,
    }).data ?? null;
  const kycState = kycUiState(me);
  const startKyc = useStartKyc();
  // The same preference the claim prompt offers, as a standing switch: on, each box reveal and
  // battle result is claimed the moment it lands, so the prompt never appears.
  const setAutoClaim = useUpdateAutoClaim();

  // Where the store ships (enabled destinations only) and the shopper's saved address. An
  // empty list means the card says so instead of offering a form.
  const destinations = shippingDestinationsOf(config);
  const shippingAddress = useShippingAddress();
  const savedAddress = shippingAddress.data?.shippingAddress ?? null;
  const [addressOpen, setAddressOpen] = useState(false);
  const [usernameOpen, setUsernameOpen] = useState(false);

  const canBuy = hasFeature("balancePurchases");
  const packs = useQuery({
    ...balancePacksQueryOptions(),
    enabled: canBuy && !!sessionToken,
  }).data;

  const opensQuery = useInfiniteQuery({
    ...opensInfiniteQueryOptions(),
    enabled: !!sessionToken,
  });
  const opens = opensQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const loadingOpens = opensQuery.isPending || opensQuery.isFetchingNextPage;
  const opensError = opensQuery.error
    ? opensQuery.error instanceof Error
      ? opensQuery.error.message
      : "failed to load opens"
    : null;

  // Battles the shopper holds a seat in, in every state: a lobby they created or joined, one
  // running, and finished ones. Battle opens are not in /v1/me/opens (they roll against the
  // battle's salt and nonce), so this is where they reach the account.
  const showBattles = hasFeature("battles");
  const battlesQuery = useInfiniteQuery({
    ...myBattlesInfiniteQueryOptions(),
    enabled: showBattles && !!sessionToken,
  });
  const battles = battlesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const loadingBattles = battlesQuery.isPending || battlesQuery.isFetchingNextPage;
  const battlesError = battlesQuery.error
    ? battlesQuery.error instanceof Error
      ? battlesQuery.error.message
      : `failed to load ${battle.plural}`
    : null;
  const boxes = useQuery({ ...boxesQueryOptions(), enabled: showBattles && battles.length > 0 });
  const boxMap = useMemo(() => {
    const map = new Map<string, RoundBox>();
    for (const box of boxes.data ?? []) map.set(box.id, { name: box.name, imageUrl: box.imageUrl });
    return map;
  }, [boxes.data]);

  const checkout = useStartCheckout();
  const buyingPackId =
    checkout.isPending || checkout.isSuccess ? (checkout.variables ?? null) : null;

  // null = no purchase in flight; "confirming" while polling after the redirect.
  const [purchaseState, setPurchaseState] = useState<
    "confirming" | "completed" | "failed" | "cancelled" | null
  >(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const { purchase, session_id: sid } = useSearch({ from: "/account" });
  const navigate = useNavigate();
  const handledReturnRef = useRef(false);

  // Checkout return: /account?purchase=success&session_id=cs_... (or cancelled).
  // The params are stripped mid-flight, so the intent is captured once (the
  // ref also survives StrictMode's double effect invocation).
  useEffect(() => {
    if (handledReturnRef.current || !sessionToken || !purchase) return;
    handledReturnRef.current = true;
    void navigate({ to: "/account", search: {}, replace: true });
    if (purchase === "cancelled") {
      track("checkout_returned", { status: "cancelled" });
      setPurchaseState("cancelled");
      return;
    }
    if (purchase === "success" && sid) {
      setPurchaseState("confirming");
      setCheckoutSessionId(sid);
    }
  }, [sessionToken, purchase, sid, navigate]);

  // Polls until the purchase leaves "pending" (webhooks usually land within
  // seconds), a 401, or the poll limit — see purchasePollQueryOptions.
  const poll = useQuery({
    ...purchasePollQueryOptions(checkoutSessionId ?? "none"),
    enabled: !!checkoutSessionId && purchaseState === "confirming",
  });
  useEffect(() => {
    if (!checkoutSessionId || purchaseState !== "confirming") return;
    const result = poll.data;
    if (!result) return;
    const terminal =
      result.kind === "unauthorized" ||
      (result.kind === "status" && result.status.status !== "pending");
    const timedOut = !terminal && result.attempt >= PURCHASE_POLL_LIMIT;
    if (!terminal && !timedOut) return;
    const outcome =
      result.kind === "status" && result.status.status === "completed" ? "completed" : "failed";
    track("checkout_returned", { status: outcome });
    setPurchaseState(outcome);
    setCheckoutSessionId(null);
    // The balance may have moved even on the timeout path.
    void queryClient.invalidateQueries({ queryKey: qk.balance });
  }, [checkoutSessionId, purchaseState, poll.data]);

  const shownError = error ?? opensError ?? battlesError;
  // GET /v1/me carries the lock; the server refuses opens, claims, and balance
  // purchases on its own, so this is the shopper-facing half of the same state.
  const locked = me?.locked ?? false;

  return (
    <div className="grid gap-6">
      <h1 className="text-2xl font-semibold tracking-tight">Account</h1>

      {locked && (
        <ErrorBanner>
          <span className="font-medium">This account is locked.</span> You can't open {box.plural},
          claim reveals, or buy {unit} while the lock is in place. Contact support to get it lifted.
        </ErrorBanner>
      )}

      {shownError && <ErrorBanner>{shownError}</ErrorBanner>}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Profile</CardTitle>
            <CardDescription>
              GET /v1/me — the Loot end user your login maps onto. Other players see your username,
              never your email.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-1.5 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Username</span>
              <span className="flex items-center gap-2">
                <span className={me?.username ? "font-medium" : "text-muted-foreground"}>
                  {me?.username ?? "Not chosen"}
                </span>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={!me}
                  onClick={() => setUsernameOpen(true)}
                >
                  {me?.username ? "Change" : "Choose"}
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={!me}
                  onClick={() => me && showProfile(me.id)}
                >
                  View profile
                </Button>
              </span>
            </div>
            <Row label="Email" value={user?.email ?? "—"} />
            <Row label="External ID" value={me?.externalId ?? "—"} mono />
            <Row label="Mode" value={me?.mode ?? "—"} />
            <Row label="Opens (nonce)" value={me ? String(me.nonce) : "—"} />
            <Row
              label="Member since"
              value={me ? new Date(me.createdAt).toLocaleDateString() : "—"}
            />
            <div className="mt-1.5 flex items-center justify-between gap-4 border-t border-border pt-3">
              <span className="grid gap-0.5">
                <span className="font-medium">Auto-claim</span>
                <span className="text-xs text-muted-foreground">
                  Claim each {box.singular} reveal and {battle.singular} result the moment it lands,
                  with no prompt.
                </span>
              </span>
              <Switch
                aria-label={`Auto-claim reveals and ${battle.singular} results`}
                checked={me?.autoClaim ?? false}
                disabled={!me || setAutoClaim.isPending}
                onCheckedChange={(checked) => {
                  setError(null);
                  setAutoClaim.mutate(checked, {
                    onError: () => setError("Could not save the auto-claim setting. Try again."),
                  });
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Balance</CardTitle>
            <CardDescription>
              GET /v1/me/balance — what you can spend, shown in the store's currency.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <p className="text-3xl font-semibold tracking-tight">
              <CurrencyIcon currency={currency} className="size-7" />{" "}
              {balance ? formatAmountWithUnit(spendableCents(balance), currency) : "—"}
            </p>
            {balance && balance.heldCents > 0 && (
              <p className="text-sm text-muted-foreground">
                {formatAmountWithUnit(balance.heldCents, currency)} held for {item.plural} you have
                revealed but not claimed yet.
              </p>
            )}
            {purchaseState === "confirming" && (
              <p className="text-sm text-muted-foreground">Confirming your purchase…</p>
            )}
            {purchaseState === "completed" && (
              <p className="text-sm text-emerald-600">Purchase complete — {unit} added.</p>
            )}
            {purchaseState === "failed" && (
              <p className="text-sm text-destructive">
                We couldn't confirm the purchase yet. If you paid, the balance updates as soon as
                the payment settles.
              </p>
            )}
            {purchaseState === "cancelled" && (
              <p className="text-sm text-muted-foreground">Checkout cancelled.</p>
            )}
            {canBuy && packs && packs.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {packs.map((pack) => (
                  <Button
                    key={pack.id}
                    variant="outline"
                    className="h-auto flex-col items-start gap-1 py-2"
                    disabled={locked || buyingPackId !== null || purchaseState === "confirming"}
                    onClick={() => {
                      setError(null);
                      checkout.mutate(pack.id, {
                        onError: (err) =>
                          setError(
                            err.message === "account_locked"
                              ? "This account is locked. Contact support."
                              : err.message === "fraud_declined"
                                ? "This purchase could not be completed. Contact support."
                                : "could not start checkout",
                          ),
                      });
                    }}
                  >
                    <span className="font-medium">{pack.name}</span>
                    <span className="text-xs text-muted-foreground">
                      <CurrencyIcon currency={currency} className="size-3" />{" "}
                      {formatAmountWithUnit(pack.creditedCents, currency)} ·{" "}
                      {formatAmount(pack.priceCents, USD)}
                      {buyingPackId === pack.id ? " · Redirecting…" : ""}
                    </span>
                  </Button>
                ))}
              </div>
            )}
            <RedeemGiftCodeForm disabled={!balance || locked} />
          </CardContent>
        </Card>
      </div>

      {kycRequired && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Identity verification</CardTitle>
            <CardDescription>
              This store verifies your identity before {item.plural} can be shipped to you.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm">
            {kycState === "approved" && (
              <p className="text-emerald-600">Your identity is verified.</p>
            )}
            {kycState === "processing" && (
              <p className="text-muted-foreground">
                Identity verification is pending. This page updates as soon as a decision arrives.
              </p>
            )}
            {kycState === "declined" && (
              <p className="text-destructive">
                Identity verification was denied. Contact support for additional information.
              </p>
            )}
            {kycState === "canStart" && (
              <>
                <p className="text-muted-foreground">
                  Verify with a <span className="font-medium">physical</span> ID document. A photo
                  of an ID, or an ID shown on a screen, is rejected and can lock your account.
                </p>
                <p className="text-xs text-muted-foreground">
                  Verification opens in a new tab. If nothing opens, allow pop-ups for this site.
                </p>
              </>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={kycState === "canStart" ? "default" : "outline"}
                disabled={kycState !== "canStart" || locked || startKyc.isPending}
                onClick={() => {
                  setError(null);
                  startKyc.mutate(undefined, {
                    onError: (err) =>
                      setError(
                        err instanceof Error && err.message
                          ? `Could not start identity verification: ${err.message}`
                          : "Could not start identity verification. Try again in a moment.",
                      ),
                  });
                }}
              >
                {kycState === "approved"
                  ? "Identity verified"
                  : kycState === "processing"
                    ? "Pending"
                    : kycState === "declined"
                      ? "Denied"
                      : startKyc.isPending
                        ? "Opening…"
                        : "Verify identity"}
              </Button>
              <span className="text-xs text-muted-foreground">Powered by Veriff</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Shipping address</CardTitle>
          <CardDescription>
            GET /v1/me/shipping-address — where your withdrawn {item.plural} ship. Each order keeps
            its own copy, so a change here never moves an order already placed.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            {shippingAddress.isPending && sessionToken ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : savedAddress ? (
              <address className="grid gap-0.5 not-italic">
                {addressLines(savedAddress).map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </address>
            ) : (
              <p className="text-muted-foreground">No address on file.</p>
            )}
            {destinations.length > 0 && (
              <Button
                variant={savedAddress ? "outline" : "default"}
                size="sm"
                disabled={locked || shippingAddress.isPending}
                onClick={() => setAddressOpen(true)}
              >
                {savedAddress ? "Edit address" : "Add address"}
              </Button>
            )}
          </div>
          {destinations.length === 0 ? (
            <p className="text-muted-foreground">Shipping is currently unavailable.</p>
          ) : (
            <div className="grid gap-1.5">
              <p className="text-xs font-medium text-muted-foreground">Where we ship</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead className="text-right">Shipping</TableHead>
                    <TableHead>Delivery</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {destinations.map((destination) => (
                    <TableRow
                      key={destination.countryCode}
                      className={
                        savedAddress?.country === destination.countryCode
                          ? "font-medium"
                          : undefined
                      }
                    >
                      <TableCell>{countryDisplayName(destination.countryCode)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {destination.costCents === 0
                          ? "Free"
                          : formatAmountWithUnit(destination.costCents, currency)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {destination.deliveryEstimate}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {addressOpen && (
        <ShippingAddressDialog
          open={addressOpen}
          onOpenChange={setAddressOpen}
          address={savedAddress}
          destinations={destinations}
        />
      )}
      {usernameOpen && me && (
        <UsernameDialog open={usernameOpen} onOpenChange={setUsernameOpen} username={me.username} />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Open history</CardTitle>
          <CardDescription>
            GET /v1/me/opens — every pull links to its on-chain-style verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          {opens.length === 0 && !loadingOpens ? (
            <p className="text-sm text-muted-foreground">
              No opens yet —{" "}
              <Link className="underline" to="/boxes">
                browse {box.plural}
              </Link>
              .
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{capitalizeTerm(item.singular)}</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {opens.map((open) => (
                  <TableRow key={open.id}>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <BoxImage
                          src={open.item.imageUrl}
                          className="size-8 rounded-md object-cover"
                        />
                        <span className="font-medium">{open.item.name}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatAmount(open.item.value, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatAmount(open.costCents, currency)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(open.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        render={<Link to="/fairness" search={{ openId: open.id }} />}
                      >
                        Verify
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {opensQuery.hasNextPage && (
            <Button
              variant="secondary"
              disabled={loadingOpens}
              onClick={() => void opensQuery.fetchNextPage()}
            >
              {loadingOpens ? "Loading…" : "Load more"}
            </Button>
          )}
        </CardContent>
      </Card>

      {showBattles && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{capitalizeTerm(battle.singular)} history</CardTitle>
            <CardDescription>
              GET /v1/me/battles — every {battle.singular} you hold a seat in; the winner takes
              every {item.singular}.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {battles.length === 0 && !loadingBattles ? (
              <p className="text-sm text-muted-foreground">
                No {battle.plural} yet —{" "}
                <Link className="underline" to="/battles">
                  start one
                </Link>
                .
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{capitalizeTerm(battle.singular)}</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead className="text-right">Pot</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {battles.map((battle) => {
                    const seat = viewerSeat(battle, me?.id);
                    const outcome = battleOutcome(battle, seat);
                    // Σ every seat's pulls; null until the battle completed.
                    const potCents =
                      battle.status === "completed"
                        ? battle.seats.reduce((sum, s) => sum + (s.totalValueCents ?? 0), 0)
                        : null;
                    return (
                      <TableRow key={battle.id}>
                        <TableCell>
                          <span className="flex flex-wrap items-center gap-2">
                            <BattleRoundsStrip rounds={battle.rounds} boxes={boxMap} max={4} />
                            <BattleModeBadge mode={battle.mode} />
                            <span className="text-xs text-muted-foreground">
                              {battle.roundCount} {battle.roundCount === 1 ? "round" : "rounds"} ·{" "}
                              {battle.playerCount} players
                            </span>
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              outcome === "won"
                                ? "default"
                                : outcome === "lost"
                                  ? "outline"
                                  : "secondary"
                            }
                          >
                            {BATTLE_OUTCOME_LABEL[outcome]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {potCents === null ? "—" : formatAmount(potCents, currency)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {formatAmount(battle.priceCents, currency)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(battle.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant="outline"
                            render={
                              <Link to="/battles/$battleId" params={{ battleId: battle.id }} />
                            }
                          >
                            {outcome === "waiting"
                              ? "Lobby"
                              : outcome === "in-progress"
                                ? "Watch"
                                : "Results"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {battlesQuery.hasNextPage && (
              <Button
                variant="secondary"
                disabled={loadingBattles}
                onClick={() => void battlesQuery.fetchNextPage()}
              >
                {loadingBattles ? "Loading…" : "Load more"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "truncate font-mono text-xs" : undefined}>{value}</span>
    </div>
  );
}
